import { Check, MessageSquarePlus, Reply, Trash2, Undo2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  type CommentRow,
  useCreateComment,
  useDeleteComment,
  useUpdateComment,
  useVersionComments,
} from "@/hooks/use-comments";
import { cn } from "@/lib/utils";

interface CommentsSectionProps {
  versionId: string;
  elementId: string;
  elementType: string;
  currentUserId: string | null;
  /** When true, no add/reply/resolve UI is shown — display only. */
  readOnly?: boolean;
}

function formatRelative(iso: string): string {
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  const minutes = Math.round(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
}

function Avatar({ name, image }: { name: string; image: string | null }) {
  if (image) {
    return <img src={image} alt={name} className="h-6 w-6 rounded-full object-cover shrink-0" />;
  }
  return (
    <div className="h-6 w-6 rounded-full bg-primary/15 text-primary text-[10px] font-medium flex items-center justify-center shrink-0">
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

function CommentItem({
  comment,
  versionId,
  currentUserId,
  onReply,
  isReplying,
  replyValue,
  setReplyValue,
  onSubmitReply,
  onCancelReply,
  readOnly,
}: {
  comment: CommentRow;
  versionId: string;
  currentUserId: string | null;
  onReply: (id: string | null) => void;
  isReplying: boolean;
  replyValue: string;
  setReplyValue: (v: string) => void;
  onSubmitReply: () => void;
  onCancelReply: () => void;
  readOnly?: boolean;
}) {
  const update = useUpdateComment(versionId);
  const remove = useDeleteComment(versionId);
  const isMine = currentUserId && currentUserId === comment.authorId;

  return (
    <div
      className={cn(
        "rounded-md border bg-card px-3 py-2 text-xs space-y-1.5",
        comment.resolved && "opacity-60",
      )}
    >
      <div className="flex items-start gap-2">
        <Avatar name={comment.authorName} image={comment.authorImage} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{comment.authorName}</span>
            <span className="text-muted-foreground">{formatRelative(comment.createdAt)}</span>
            {comment.resolved && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 rounded bg-success-soft text-success-ink">
                resolved
              </span>
            )}
          </div>
          <div className="whitespace-pre-wrap break-words mt-0.5 text-sm">{comment.body}</div>
        </div>
      </div>

      {!readOnly && (
        <div className="flex items-center gap-1 pl-8">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-[11px]"
            onClick={() => onReply(isReplying ? null : comment.id)}
          >
            <Reply className="h-3 w-3 mr-1" /> Reply
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-[11px]"
            onClick={() => update.mutate({ commentId: comment.id, resolved: !comment.resolved })}
          >
            {comment.resolved ? (
              <>
                <Undo2 className="h-3 w-3 mr-1" /> Reopen
              </>
            ) : (
              <>
                <Check className="h-3 w-3 mr-1" /> Resolve
              </>
            )}
          </Button>
          {isMine && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[11px] text-muted-foreground hover:text-destructive"
              onClick={() => remove.mutate(comment.id)}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      )}

      {isReplying && !readOnly && (
        <div className="pl-8 space-y-1.5">
          <Textarea
            value={replyValue}
            onChange={(e) => setReplyValue(e.target.value)}
            rows={2}
            placeholder="Write a reply…"
            className="text-xs"
          />
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              className="h-6 px-2 text-[11px]"
              onClick={onSubmitReply}
              disabled={!replyValue.trim()}
            >
              Reply
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[11px]"
              onClick={onCancelReply}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function CarriedOverCard({ comment }: { comment: CommentRow }) {
  return (
    <div className="rounded-md border border-dashed border-warning/30 bg-warning-soft px-3 py-2 text-xs space-y-1">
      <div className="flex items-start gap-2">
        <Avatar name={comment.authorName} image={comment.authorImage} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{comment.authorName}</span>
            <span className="text-muted-foreground">{formatRelative(comment.createdAt)}</span>
            <span className="text-[10px] uppercase tracking-wider px-1.5 rounded bg-warning-soft text-warning-ink">
              from v{comment.versionNumber}
            </span>
          </div>
          <div className="whitespace-pre-wrap break-words mt-0.5 text-sm">{comment.body}</div>
        </div>
      </div>
    </div>
  );
}

export function CommentsSection({
  versionId,
  elementId,
  elementType,
  currentUserId,
  readOnly,
}: CommentsSectionProps) {
  const { data, isLoading } = useVersionComments(versionId);
  const create = useCreateComment(versionId);
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState("");

  const own = data?.own ?? [];
  const carried = data?.carriedOver ?? [];

  const { topLevel, repliesByParent } = useMemo(() => {
    const elementOwn = own.filter((c) => c.elementId === elementId);
    const top = elementOwn.filter((c) => !c.parentId);
    const grouped = new Map<string, CommentRow[]>();
    for (const c of elementOwn) {
      if (c.parentId) {
        if (!grouped.has(c.parentId)) grouped.set(c.parentId, []);
        grouped.get(c.parentId)?.push(c);
      }
    }
    return { topLevel: top, repliesByParent: grouped };
  }, [own, elementId]);

  const elementCarryOver = carried.filter((c) => c.elementId === elementId);

  const submitTop = () => {
    if (!draft.trim()) return;
    create.mutate(
      { elementId, elementType, body: draft.trim() },
      {
        onSuccess: () => setDraft(""),
      },
    );
  };

  const submitReply = () => {
    if (!replyDraft.trim() || !replyTo) return;
    create.mutate(
      { elementId, elementType, body: replyDraft.trim(), parentId: replyTo },
      {
        onSuccess: () => {
          setReplyDraft("");
          setReplyTo(null);
        },
      },
    );
  };

  return (
    <div className="space-y-2">
      {isLoading && <div className="text-xs text-muted-foreground italic">Loading comments…</div>}

      {topLevel.length === 0 && elementCarryOver.length === 0 && !isLoading && (
        <div className="text-xs text-muted-foreground italic">No comments yet.</div>
      )}

      {topLevel.map((c) => (
        <div key={c.id} className="space-y-1.5">
          <CommentItem
            comment={c}
            versionId={versionId}
            currentUserId={currentUserId}
            onReply={setReplyTo}
            isReplying={replyTo === c.id}
            replyValue={replyDraft}
            setReplyValue={setReplyDraft}
            onSubmitReply={submitReply}
            onCancelReply={() => {
              setReplyTo(null);
              setReplyDraft("");
            }}
            readOnly={readOnly}
          />
          {(repliesByParent.get(c.id) ?? []).map((r) => (
            <div key={r.id} className="pl-4 border-l-2 border-border ml-2">
              <CommentItem
                comment={r}
                versionId={versionId}
                currentUserId={currentUserId}
                onReply={setReplyTo}
                isReplying={false}
                replyValue=""
                setReplyValue={() => {}}
                onSubmitReply={() => {}}
                onCancelReply={() => {}}
                readOnly={readOnly}
              />
            </div>
          ))}
        </div>
      ))}

      {elementCarryOver.length > 0 && (
        <div className="space-y-1.5 pt-1">
          <div className="text-[10px] uppercase tracking-wider text-warning-ink">
            Carried over · unresolved on previous versions
          </div>
          {elementCarryOver.map((c) => (
            <CarriedOverCard key={c.id} comment={c} />
          ))}
        </div>
      )}

      {!readOnly && (
        <div className="space-y-1.5 pt-1">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            placeholder="Add a comment…"
            className="text-xs"
          />
          <Button
            size="sm"
            className="h-7"
            onClick={submitTop}
            disabled={!draft.trim() || create.isPending}
          >
            <MessageSquarePlus className="h-3.5 w-3.5 mr-1.5" />
            Post comment
          </Button>
        </div>
      )}
    </div>
  );
}
